// AudioManager — procedural sound via the Web Audio API. No audio files:
// every sound is synthesized at runtime. This matches the procedural ethos
// of the rest of the project (terrain, trees, physics are all generated),
// keeps the asset pipeline empty, and sidesteps sample licensing.
//
// Cues:
//   wind    — continuous noise rush; gain + brightness tracked to fall speed
//   whoosh  — one-shot band-passed noise sweep on jump
//
// (Three cues were prototyped and cut: a cord-catch "twang" that never read
// as a twang, a water "splash" that rarely fired since the player seldom
// dips below sea level, and a detuned-saw "ambient" drone pad that was
// inaudible under the wind. The Level still emits a `catch` event — see
// level.js — so a future cue can hang off it without re-deriving detection.)
//
// Autoplay policy: an AudioContext started outside a user gesture is born
// 'suspended' and logs a warning. So the context is created lazily on the
// first resume() call, which main.js wires to the MENU→PLAYING transition —
// that transition is always driven by a click or keypress.
//
// Every public method no-ops cleanly if Web Audio is unavailable or the
// context hasn't been started yet, so callers never need to guard.

// Reference speed (world units/sec) that maps to "full rush". The player's
// drag-limited terminal speed is ~265 (see physics.js: 0.1·v² = mass·g),
// so 280 lands a hard fall near the top of the wind curve.
const SPEED_REF = 280;

export class AudioManager {
	constructor() {
		this.ctx = null;
		this.master = null;

		// Wind chain (persistent).
		this._windGain = null;
		this._windFilter = null;

		// Shared white-noise buffer, reused by every one-shot.
		this._noiseBuf = null;

		this._suspendTimer = null;
	}

	// ─── Lifecycle ──────────────────────────────────────────────

	// Build the audio graph. Must run inside a user gesture. Idempotent.
	_init() {
		if (this.ctx) return;
		const AC = window.AudioContext || window.webkitAudioContext;
		if (!AC) return; // no Web Audio — every method below stays a no-op
		this.ctx = new AC();

		// Master bus: gain → soft compressor → speakers. The compressor is a
		// cheap safety net so overlapping cues (whoosh + wind + pad) can't clip.
		this.master = this.ctx.createGain();
		this.master.gain.value = 0.9;
		const comp = this.ctx.createDynamicsCompressor();
		this.master.connect(comp);
		comp.connect(this.ctx.destination);

		this._noiseBuf = this._makeNoiseBuffer(2.0);
		this._buildWind();
	}

	// Called on MENU→PLAYING and on un-pause. Starts/resumes the context and
	// fades the master back in.
	resume() {
		this._init();
		if (!this.ctx) return;
		clearTimeout(this._suspendTimer);
		this.ctx.resume();
		const now = this.ctx.currentTime;
		this.master.gain.setTargetAtTime(0.9, now, 0.12);
	}

	// Called on PLAYING→PAUSED. Ducks the master, then suspends the context
	// after the fade so paused gameplay costs no audio CPU.
	pause() {
		if (!this.ctx) return;
		const now = this.ctx.currentTime;
		this.master.gain.setTargetAtTime(0.0001, now, 0.08);
		this._suspendTimer = setTimeout(() => {
			if (this.ctx) this.ctx.suspend();
		}, 250);
	}

	// ─── Continuous: wind ───────────────────────────────────────

	// Map current player speed → wind loudness + brightness. Smoothed with
	// setTargetAtTime so there's no zipper noise on fast speed changes.
	setSpeed(speed) {
		if (!this.ctx || !this._windGain) return;
		const t = Math.min(speed / SPEED_REF, 1);
		const gain = 0.03 + t * 0.45;       // faint breeze → full rush
		const cutoff = 250 + t * 3200;      // muffled → airy as you accelerate
		const now = this.ctx.currentTime;
		this._windGain.gain.setTargetAtTime(gain, now, 0.08);
		this._windFilter.frequency.setTargetAtTime(cutoff, now, 0.08);
	}

	_buildWind() {
		const src = this.ctx.createBufferSource();
		src.buffer = this._noiseBuf;
		src.loop = true;

		const filter = this.ctx.createBiquadFilter();
		filter.type = 'lowpass';
		filter.frequency.value = 250;
		filter.Q.value = 0.7;

		const gain = this.ctx.createGain();
		gain.gain.value = 0; // setSpeed ramps this up once playing

		src.connect(filter);
		filter.connect(gain);
		gain.connect(this.master);
		src.start();

		this._windFilter = filter;
		this._windGain = gain;
	}

	// ─── One-shots ──────────────────────────────────────────────

	// Jump: a band-passed noise burst whose centre frequency sweeps up then
	// back down — a "whoosh". Louder the faster you launch.
	whoosh(speed = 0) {
		if (!this.ctx) return;
		const now = this.ctx.currentTime;
		const src = this.ctx.createBufferSource();
		src.buffer = this._noiseBuf;

		const bp = this.ctx.createBiquadFilter();
		bp.type = 'bandpass';
		bp.Q.value = 0.8;
		bp.frequency.setValueAtTime(300, now);
		bp.frequency.linearRampToValueAtTime(1600, now + 0.18);
		bp.frequency.linearRampToValueAtTime(500, now + 0.5);

		const g = this.ctx.createGain();
		const vol = Math.min(0.15 + (speed / SPEED_REF) * 0.5, 0.7);
		g.gain.setValueAtTime(0.0001, now);
		g.gain.exponentialRampToValueAtTime(vol, now + 0.06);
		g.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);

		src.connect(bp);
		bp.connect(g);
		g.connect(this.master);
		src.start(now);
		src.stop(now + 0.6);
		src.onended = () => { src.disconnect(); bp.disconnect(); g.disconnect(); };
	}

	// ─── Helpers ────────────────────────────────────────────────

	_makeNoiseBuffer(seconds) {
		const len = Math.floor(this.ctx.sampleRate * seconds);
		const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
		const d = buf.getChannelData(0);
		for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
		return buf;
	}
}
